import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, RefreshCw, Printer, Volume2, VolumeX, Monitor } from "lucide-react";
import api, { money } from "@/lib/api";
import { printCustomerReceipt, printKitchenTicket } from "@/lib/receipt";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { getSocket } from "@/lib/socket";

const FLOW = ["NEW", "PREPARING", "READY", "COMPLETED"];
const NEXT = { NEW: "PREPARING", PREPARING: "READY", READY: "COMPLETED" };

function chime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [880, 1175];
    notes.forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.4, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.start(t); o.stop(t + 0.36);
    });
  } catch { /* ignore */ }
}

export default function StaffOrders() {
  const { user, loading, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [sound, setSound] = useState(true);
  const [, setAckTick] = useState(0);
  const seenRef = useRef(null);
  const ackRef = useRef(new Set());
  const ordersRef = useRef([]);
  const soundRef = useRef(true);

  const acknowledge = useCallback((o) => {
    if (o.status !== "NEW") return;
    if (!ackRef.current.has(o.order_number)) { ackRef.current.add(o.order_number); setAckTick((t) => t + 1); }
  }, []);

  const load = useCallback(() => {
    api.get("/staff/orders", { params: { _: Date.now() } }).then(({ data }) => {
      const activeNew = data.filter((o) => o.status === "NEW").map((o) => o.order_number);
      if (seenRef.current !== null) {
        const fresh = activeNew.filter((n) => !seenRef.current.includes(n));
        if (fresh.length && sound) { chime(); if (navigator.vibrate) navigator.vibrate(200); toast.success(`New order: ${fresh[0]}`); }
      }
      seenRef.current = activeNew;
      setOrders(data);
    }).catch(() => {});
  }, [sound]);

  useEffect(() => {
    if (loading) return;
    if (!user || !["staff", "branch_admin", "admin", "super_admin"].includes(user.role)) { navigate("/login"); return; }
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [user, loading, load, navigate]);

  useEffect(() => {
    const s = getSocket();
    const onEvt = () => load();
    s.on("new_order", onEvt);
    s.on("order_updated", onEvt);
    return () => { s.off("new_order", onEvt); s.off("order_updated", onEvt); };
  }, [load]);

  useEffect(() => { ordersRef.current = orders; }, [orders]);
  useEffect(() => { soundRef.current = sound; }, [sound]);

  // Repeating alarm: keep chiming every 7s while any NEW order is unacknowledged.
  useEffect(() => {
    const t = setInterval(() => {
      if (!soundRef.current) return;
      const unack = ordersRef.current.some((o) => o.status === "NEW" && !ackRef.current.has(o.order_number));
      if (unack) { chime(); if (navigator.vibrate) navigator.vibrate(200); }
    }, 7000);
    return () => clearInterval(t);
  }, []);

  const setStatus = async (o, status) => {
    acknowledge(o);
    await api.patch(`/staff/orders/${o.order_number}/status`, { status });
    toast.success(`${o.order_number} → ${status}`);
    load();
  };
  const advance = async (o) => {
    const next = NEXT[o.status];
    if (!next) return;
    await setStatus(o, next);
  };
  const reject = async (o) => {
    if (!window.confirm(`Reject order ${o.order_number}?`)) return;
    await setStatus(o, "CANCELLED");
  };

  if (loading || !user) return null;
  const active = orders.filter((o) => o.status !== "COMPLETED" && o.status !== "CANCELLED");
  const newOrders = active.filter((o) => o.status === "NEW");
  const unackCount = newOrders.filter((o) => !ackRef.current.has(o.order_number)).length;

  return (
    <div className="min-h-screen bg-metros-black text-white">
      <header className="px-4 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <h1 className="font-heading font-extrabold text-lg">Kitchen — Live Orders</h1>
          {newOrders.length > 0 && (
            <span data-testid="staff-new-badge" className={`text-sm font-extrabold px-3 py-1 rounded-full bg-metros-red text-white ${unackCount > 0 ? "animate-pulse ring-2 ring-yellow-300" : ""}`}>{newOrders.length} NEW</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="staff-sound-toggle" onClick={() => setSound((s) => !s)} className="text-white/70 hover:text-white" title="Toggle sound">{sound ? <Volume2 size={18} /> : <VolumeX size={18} />}</button>
          <button data-testid="staff-display-btn" onClick={() => navigate("/staff/display")} className="flex items-center gap-1 text-sm text-white/70 hover:text-white" title="Customer display"><Monitor size={16} /> Display</button>
          <button data-testid="staff-refresh" onClick={load} className="text-white/70 hover:text-white"><RefreshCw size={18} /></button>
          <button data-testid="staff-logout" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1 text-sm text-white/70 hover:text-white"><LogOut size={16} /> Sign Out</button>
        </div>
      </header>
      <div className="max-w-5xl mx-auto p-4">
        {active.length === 0 && <p className="text-center text-white/50 py-20" data-testid="staff-no-orders">No active orders right now.</p>}
        <div className="grid md:grid-cols-2 gap-3">
          {active.map((o) => {
            const isNew = o.status === "NEW";
            const flashing = isNew && !ackRef.current.has(o.order_number);
            return (
            <div key={o.id} data-testid={`staff-order-${o.order_number}`} onClick={() => isNew && acknowledge(o)}
              className={`bg-white/5 border rounded-2xl p-4 transition-all ${flashing ? "border-yellow-300 ring-2 ring-yellow-300 animate-pulse cursor-pointer" : isNew ? "border-green-500/50" : "border-white/10"}`}>
              <div className="flex items-center justify-between">
                <span className="font-heading font-extrabold text-lg">{o.order_number}</span>
                <span className="text-xs bg-metros-red px-2 py-1 rounded-full font-bold">{o.status}</span>
              </div>
              <p className="text-xs text-white/50 mt-1">{o.type} · {new Date(o.created_at).toLocaleTimeString()}</p>
              <div className="mt-2 space-y-0.5 text-sm">
                {o.items.map((i, k) => <p key={k}>{i.quantity}× {i.name}</p>)}
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="font-bold">{money(o.total)}</span>
                <div className="flex flex-wrap gap-2 justify-end">
                  <button data-testid={`staff-print-${o.order_number}`} onClick={() => printCustomerReceipt(o, settings)} className="flex items-center gap-1 text-sm bg-white/10 rounded-full px-3 py-2"><Printer size={14} /> Receipt</button>
                  <button data-testid={`staff-kitchen-${o.order_number}`} onClick={() => printKitchenTicket(o)} className="flex items-center gap-1 text-sm bg-white/10 rounded-full px-3 py-2"><Printer size={14} /> Kitchen</button>
                  {o.status === "NEW" ? (
                    <>
                      <button data-testid={`staff-reject-${o.order_number}`} onClick={() => reject(o)} className="text-sm bg-white/10 hover:bg-white/20 rounded-full px-4 py-2 font-bold">Reject</button>
                      <button data-testid={`staff-accept-${o.order_number}`} onClick={() => advance(o)} className="text-sm bg-green-600 rounded-full px-4 py-2 font-bold">Accept</button>
                    </>
                  ) : (
                    NEXT[o.status] && <button data-testid={`staff-advance-${o.order_number}`} onClick={() => advance(o)} className="text-sm bg-metros-red rounded-full px-4 py-2 font-bold">Mark {NEXT[o.status]}</button>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
